import { z } from "zod";
import { memoryVersionIdSchema } from "./memories.js";
export const calculationDecisionKindValues = [
    "metric",
    "entity",
    "source_dataset",
    "status_treatment",
    "accounting_basis",
    "credit_note_treatment",
    "currency",
    "timezone",
    "date_semantics",
    "formula_numerator",
    "formula_denominator",
    "grouping_grain",
    "null_handling",
    "zero_handling",
];
export const calculationDecisionKindSchema = z.enum(calculationDecisionKindValues);
const resolvedDecisionValueSchema = z.string()
    .trim()
    .min(1)
    .max(2_000);
const calculationArgumentPathSchema = z.string()
    .max(500)
    .regex(/^(?:\/(?:[^~/]|~[01])*)+$/u, "Expected a non-root JSON pointer");
export const calculationApplicationTargetSchema = z.object({
    kind: z.literal("tool_argument"),
    argumentPath: calculationArgumentPathSchema,
}).strict();
export const calculationDecisionSchema = z.object({
    kind: calculationDecisionKindSchema,
    resolution: z.literal("resolved"),
    value: resolvedDecisionValueSchema,
    appliedMemoryIds: z.array(memoryVersionIdSchema).max(25),
    applicationTarget: calculationApplicationTargetSchema.nullable(),
}).strict().superRefine((decision, context) => {
    const seen = new Set();
    for (const [index, versionId] of decision.appliedMemoryIds.entries()) {
        if (!seen.has(versionId)) {
            seen.add(versionId);
            continue;
        }
        context.addIssue({
            code: "custom",
            path: ["appliedMemoryIds", index],
            message: "A decision may bind an immutable memory version only once",
        });
    }
    if (decision.appliedMemoryIds.length > 0 && decision.applicationTarget === null) {
        context.addIssue({
            code: "custom",
            path: ["applicationTarget"],
            message: "A memory-bound decision must identify the exact executed argument",
        });
    }
    if (decision.appliedMemoryIds.length === 0 && decision.applicationTarget !== null) {
        context.addIssue({
            code: "custom",
            path: ["applicationTarget"],
            message: "An unbound decision cannot claim an application target",
        });
    }
});
/**
 * A complete, resolved v1 calculation plan. Memory bindings live on the
 * exact decisions they informed; the top-level list is a redundant audit
 * index and must equal their unique union.
 */
export const calculationContractSchema = z.object({
    schemaVersion: z.literal(1),
    kind: z.enum(["metric", "reconciliation"]),
    decisions: z.array(calculationDecisionSchema)
        .length(calculationDecisionKindValues.length),
    appliedMemoryIds: z.array(memoryVersionIdSchema).max(25),
}).strict().superRefine((contract, context) => {
    const decisionKinds = new Set();
    let decisionBindingCount = 0;
    for (const [index, decision] of contract.decisions.entries()) {
        decisionBindingCount += decision.appliedMemoryIds.length;
        if (decisionKinds.has(decision.kind)) {
            context.addIssue({
                code: "custom",
                path: ["decisions", index, "kind"],
                message: `Calculation decision ${decision.kind} appears more than once`,
            });
        }
        decisionKinds.add(decision.kind);
    }
    if (decisionBindingCount > 100) {
        context.addIssue({
            code: "custom",
            path: ["decisions"],
            message: "A calculation contract may contain at most 100 decision bindings",
        });
    }
    for (const requiredKind of calculationDecisionKindValues) {
        if (!decisionKinds.has(requiredKind)) {
            context.addIssue({
                code: "custom",
                path: ["decisions"],
                message: `Calculation decision ${requiredKind} is required`,
            });
        }
    }
    const topLevel = new Set();
    for (const [index, versionId] of contract.appliedMemoryIds.entries()) {
        if (topLevel.has(versionId)) {
            context.addIssue({
                code: "custom",
                path: ["appliedMemoryIds", index],
                message: "Top-level applied memory versions must be unique",
            });
        }
        topLevel.add(versionId);
    }
    const decisionUnion = new Set(contract.decisions.flatMap((decision) => decision.appliedMemoryIds));
    if (topLevel.size !== decisionUnion.size ||
        [...topLevel].some((versionId) => !decisionUnion.has(versionId))) {
        context.addIssue({
            code: "custom",
            path: ["appliedMemoryIds"],
            message: "Top-level appliedMemoryIds must exactly equal the unique union of decision bindings",
        });
    }
});
